import React, { useEffect, useState } from 'react';

import { useTranslation } from 'react-i18next';

import { useUnsavedChangesGuard } from '@/hooks/use-unsaved-changes-guard';
import { OrganizationSettingsTemplate } from '@/modules/common/components/templates';
import { OnboardingBottomSection } from '@/modules/onboarding/common/components/onboarding-bottom-section';
import { useOnboardingUnsavedChangesStore } from '@/modules/onboarding/common/state/use-onboarding-unsaved-changes-store';
import { UnsavedChangesModal } from '@/modules/settings/components/unsaved-changes-modal';
import { useRootSelector } from '@/router/state/root/util/context/helpers';

interface OrganizationOnboardingBottomSectionProps {
  isDirty: boolean;
  isLoading: boolean;
  isDisabled: boolean;
  submitButtonText: string;
  onSubmit: () => void;
  onDirtyChange: (isDirty: boolean) => void;
}

function OrganizationOnboardingBottomSection({
  isDirty,
  isLoading,
  isDisabled,
  submitButtonText,
  onSubmit,
  onDirtyChange,
}: OrganizationOnboardingBottomSectionProps) {
  useEffect(() => {
    onDirtyChange(isDirty);
  }, [isDirty, onDirtyChange]);

  return (
    <>
      <div className="pb-[72px]" />
      <OnboardingBottomSection
        nextButtonProps={{
          text: submitButtonText,
          onClick: onSubmit,
          isLoading,
          isDisabled,
        }}
      />
    </>
  );
}

export const OrganizationOnboardingStepPage: React.FC = () => {
  const state = useRootSelector(state => state);
  const { onboardingMachine } = state.children;
  const [isDirty, setIsDirty] = useState(false);
  const setSidebarIsDirty = useOnboardingUnsavedChangesStore(state => state.setIsDirty);
  const resetSidebarDirty = useOnboardingUnsavedChangesStore(state => state.reset);

  const { isModalOpen, handleModalCancel, handleModalDiscard, allowNavigation } =
    useUnsavedChangesGuard({
      isDirty,
      onDiscard: () => setIsDirty(false),
    });

  const { t } = useTranslation(['common', 'authenticated.organization']);

  useEffect(() => {
    setSidebarIsDirty(isDirty);

    return () => {
      resetSidebarDirty();
    };
  }, [isDirty, setSidebarIsDirty, resetSidebarDirty]);

  return (
    <div className="mb-6 flex w-full flex-col">
      <div className="w-full max-w-3xl px-12 pt-8">
        <h1 className="mb-2 text-2xl font-semibold">{t('authenticated.organization:title')}</h1>
        <p className="mb-6 text-sm text-muted-foreground">
          {t('authenticated.organization:description')}
        </p>

        <OrganizationSettingsTemplate
          onSuccess={() => {
            allowNavigation();
            onboardingMachine?.send({ type: 'NEXT' });
          }}
          submitButtonText={t('authenticated.organization:submit')}
          showSuccessToast={false}
          renderBottomSection={({ onSubmit, isLoading, isDisabled, submitButtonText, isDirty }) => (
            <OrganizationOnboardingBottomSection
              isDirty={isDirty}
              isLoading={isLoading}
              isDisabled={isDisabled}
              submitButtonText={submitButtonText}
              onSubmit={onSubmit}
              onDirtyChange={setIsDirty}
            />
          )}
        />
      </div>
      <UnsavedChangesModal
        open={isModalOpen}
        onClose={handleModalCancel}
        onDiscard={handleModalDiscard}
      />
    </div>
  );
};
